import { useState, useEffect } from 'react';
import { Clock, AlertCircle, AlertTriangle } from 'lucide-react';
import { ticketService, TicketWithUser } from '../../../services/ticketService';

export type StatusFilter = 'all' | 'open' | 'in_progress' | 'resolved' | 'closed';
export type PriorityFilter = 'all' | 'low' | 'medium' | 'high' | 'urgent';
export type IssueTypeFilter = 'all' | 'credits' | 'subscription' | 'billing' | 'technical' | 'other';

const ITEMS_PER_PAGE = 10;

export function useAdminTicketsPage() {
  const [tickets, setTickets] = useState<TicketWithUser[]>([]);
  const [filteredTickets, setFilteredTickets] = useState<TicketWithUser[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [priorityFilter, setPriorityFilter] = useState<PriorityFilter>('all');
  const [issueTypeFilter, setIssueTypeFilter] = useState<IssueTypeFilter>('all');
  const [selectedTicket, setSelectedTicket] = useState<TicketWithUser | null>(null);
  const [adminResponse, setAdminResponse] = useState('');
  const [updating, setUpdating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<TicketWithUser | null>(null);
  const [deleting, setDeleting] = useState(false);

  const getFilenameFromUrl = (url: string): string => {
    try {
      const urlObj = new URL(url.startsWith('/api/') ? `${process.env.REACT_APP_API_URL || ''}${url}` : url);
      const pathname = urlObj.pathname;
      const filename = pathname.split('/').pop() || 'image';
      return filename.length > 30 ? filename.substring(0, 30) + '...' : filename;
    } catch {
      const parts = url.split('/');
      const filename = parts[parts.length - 1] || 'image';
      return filename.length > 30 ? filename.substring(0, 30) + '...' : filename;
    }
  };

  const loadTickets = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ticketService.getAllTickets(
        statusFilter === 'all' ? undefined : statusFilter,
        priorityFilter === 'all' ? undefined : priorityFilter,
        issueTypeFilter === 'all' ? undefined : issueTypeFilter
      );
      setTickets(data);
      setCurrentPage(1);
    } catch (err: any) {
      setError(err.message || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const filterTickets = () => {
    let filtered = tickets;
    if (statusFilter !== 'all') {
      filtered = filtered.filter((t) => t.status === statusFilter);
    }
    if (priorityFilter !== 'all') {
      filtered = filtered.filter((t) => t.priority === priorityFilter);
    }
    if (issueTypeFilter !== 'all') {
      filtered = filtered.filter((t) => t.issue_type === issueTypeFilter);
    }
    setFilteredTickets(filtered);
  };

  useEffect(() => {
    loadTickets();
  }, [statusFilter, priorityFilter, issueTypeFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, priorityFilter, issueTypeFilter]);

  useEffect(() => {
    filterTickets();
  }, [tickets, statusFilter, priorityFilter, issueTypeFilter]);

  const getStatusCount = (status: string) => {
    if (status === 'all') return tickets.length;
    return tickets.filter((t) => t.status === status).length;
  };

  const getPriorityCount = (priority: string) => {
    if (priority === 'all') return tickets.length;
    return tickets.filter((t) => t.priority === priority).length;
  };

  const getIssueTypeCount = (issueType: string) => {
    if (issueType === 'all') return tickets.length;
    return tickets.filter((t) => t.issue_type === issueType).length;
  };

  const formatIssueType = (issueType: string | undefined): string => {
    if (!issueType) return '';
    const predefinedTypes = ['credits', 'subscription', 'billing', 'technical', 'other'];
    if (predefinedTypes.includes(issueType.toLowerCase())) {
      return issueType === 'other' ? 'Other' : issueType.charAt(0).toUpperCase() + issueType.slice(1);
    }
    return `Other: ${issueType}`;
  };

  const handleUpdateStatus = async (ticketId: string, status: string) => {
    try {
      setUpdating(true);
      await ticketService.updateTicketAdmin(ticketId, { status });
      await loadTickets();
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket(null);
      }
    } catch (err: any) {
      alert(err.message || 'Failed to update ticket');
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdateResponse = async () => {
    if (!selectedTicket) return;
    try {
      setUpdating(true);
      await ticketService.updateTicketAdmin(selectedTicket.id, { admin_response: adminResponse });
      await loadTickets();
      setSelectedTicket(null);
      setAdminResponse('');
    } catch (err: any) {
      alert(err.message || 'Failed to update response');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteTicket = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await ticketService.deleteTicketAdmin(deleteTarget.id);
      await loadTickets();
      setDeleteTarget(null);
    } catch (err: any) {
      alert(err.message || 'Failed to delete ticket');
    } finally {
      setDeleting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      open: { bg: 'bg-yellow-500/10', text: 'text-yellow-400', border: 'border-yellow-500/20' },
      in_progress: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20' },
      resolved: { bg: 'bg-green-500/10', text: 'text-green-400', border: 'border-green-500/20' },
      closed: { bg: 'bg-gray-500/10', text: 'text-gray-400', border: 'border-gray-500/20' },
    };
    return badges[status as keyof typeof badges] || badges.open;
  };

  const getPriorityBadge = (priority: string) => {
    const badges = {
      low: { bg: 'bg-gray-500/10', text: 'text-gray-400', border: 'border-gray-500/20', icon: Clock },
      medium: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20', icon: Clock },
      high: { bg: 'bg-orange-500/10', text: 'text-orange-400', border: 'border-orange-500/20', icon: AlertCircle },
      urgent: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/20', icon: AlertTriangle },
    };
    return badges[priority as keyof typeof badges] || badges.medium;
  };

  const totalPages = Math.max(1, Math.ceil(filteredTickets.length / ITEMS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedTickets = filteredTickets.slice(
    (safePage - 1) * ITEMS_PER_PAGE,
    safePage * ITEMS_PER_PAGE
  );

  return {
    filteredTickets,
    paginatedTickets,
    currentPage,
    setCurrentPage,
    totalPages,
    itemsPerPage: ITEMS_PER_PAGE,
    loading,
    error,
    statusFilter,
    setStatusFilter,
    priorityFilter,
    setPriorityFilter,
    issueTypeFilter,
    setIssueTypeFilter,
    selectedTicket,
    setSelectedTicket,
    adminResponse,
    setAdminResponse,
    updating,
    deleteTarget,
    setDeleteTarget,
    deleting,
    loadTickets,
    getFilenameFromUrl,
    getStatusCount,
    getPriorityCount,
    getIssueTypeCount,
    formatIssueType,
    handleUpdateStatus,
    handleUpdateResponse,
    handleDeleteTicket,
    getStatusBadge,
    getPriorityBadge,
  };
}
